import { useState } from "react";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { StatCard } from "../v5/components/V5Charts";
import ExcelJS from "exceljs";
import PlatformAlerts from "../../../components/PlatformAlerts";
import { useMaponDashboard } from "../../../features/dashboards/queries";
import {
  usePlatformCriticalAlerts,
  useReportingCompanies,
} from "../../../features/dashboards/reporting";
import ReportingUnitsTables from "../../../components/dashboards/ReportingUnitsTables";
import PlatformStatsCards from "../../../components/dashboards/PlatformStatsCards";
import PlatformRiskDistribution from "../../../components/dashboards/PlatformRiskDistribution";
import AllClientsTable from "../../../components/dashboards/AllClientsTable";
import ActiveUnitsHistorySection from "../../../components/dashboards/ActiveUnitsHistorySection";
import AltasBajasPlatformCards from "../../../components/dashboards/AltasBajasPlatformCards";
import { fileDate } from "../../../features/sg/exportRows";

export default function MaponDashboard() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const maponQuery = useMaponDashboard();
  const maponCompaniesQuery = useReportingCompanies({ platform: "mapon" });
  const unitsData = maponQuery.data?.units ?? [];
  const usersData = maponQuery.data?.users ?? [];
  const unitsCount = unitsData.length;
  const usersCount = usersData.length;
  const loading = maponQuery.isLoading;

  const [unitsWithoutUpdatePage, setUnitsWithoutUpdatePage] = useState(1);
  const [unitsByCompanyPage, setUnitsByCompanyPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('asc');
  const [timeFilter, setTimeFilter] = useState<'24h' | '1week' | '1month'>('1month');
  const itemsPerPage = 10;

  const criticalAlerts = usePlatformCriticalAlerts("mapon");
  const criticalCompanies = criticalAlerts.data?.companies ?? [];
  const allUnits = criticalAlerts.data?.units ?? [];

  // Process units data for charts
  const unitsByState = unitsData.reduce((acc: Record<string, number>, unit: any) => {
    const state = unit.state?.name || 'unknown';
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});

  const unitsByType = unitsData.reduce((acc: Record<string, number>, unit: any) => {
    const type = unit.type || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});


  const unitsByCountry = unitsData.reduce((acc: Record<string, number>, unit: any) => {
    const country = unit.country_code || 'unknown';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {});

  const unitsByCompany = unitsData.reduce((acc: Record<string, number>, unit: any) => {
    const company = unit.company_id || 'unknown';
    acc[company] = (acc[company] || 0) + 1;
    return acc;
  }, {});

  // Units with data vs without data
  const unitsWithData = unitsData.filter((u: any) => u.state?.name !== 'nodata').length;
  const unitsWithoutData = unitsData.filter((u: any) => u.state?.name === 'nodata').length;

  // Units without reporting based on time filter
  const getCutoffDate = () => {
    const now = new Date();
    switch (timeFilter) {
      case '24h':
        now.setHours(now.getHours() - 24);
        return now;
      case '1week':
        now.setDate(now.getDate() - 7);
        return now;
      case '1month':
        now.setMonth(now.getMonth() - 1);
        return now;
      default:
        now.setMonth(now.getMonth() - 1);
        return now;
    }
  };

  const cutoffDate = getCutoffDate();
  const unitsWithoutRecentUpdate = unitsData.filter((u: any) => {
    if (!u.last_update) return true;
    const lastUpdate = new Date(u.last_update);
    return lastUpdate < cutoffDate;
  }).map((u: any) => ({
    number: u.number,
    unit_id: u.unit_id,
    company_id: u.company_id,
    mapon_company_name: u.mapon_company_name,
    lastUpdate: u.last_update,
    daysSinceUpdate: Math.floor((new Date().getTime() - new Date(u.last_update).getTime()) / (1000 * 60 * 60 * 24))
  })).sort((a, b) => sortOrder === 'desc' ? b.daysSinceUpdate - a.daysSinceUpdate : a.daysSinceUpdate - b.daysSinceUpdate);

  // Process users data for charts
  const usersByType = usersData.reduce((acc: Record<string, number>, user: any) => {
    const type = user.type || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const activeUsers = usersData.filter((u: any) => !u.blocked).length;
  const blockedUsers = usersData.filter((u: any) => u.blocked).length;


  // Download Excel functions
  const downloadUnitsByCompanyExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Unidades por Compañía');

    worksheet.columns = [
      { header: 'ID Compañía', key: 'companyId', width: 20 },
      { header: 'Cantidad', key: 'count', width: 15 },
      { header: 'Porcentaje', key: 'percentage', width: 15 },
    ];

    Object.entries(unitsByCompany).forEach(([company, count]) => {
      worksheet.addRow({
        companyId: company,
        count: count,
        percentage: ((count / unitsCount) * 100).toFixed(1) + '%',
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unidades_por_compania_${fileDate()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadUnitsWithoutUpdateExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Unidades sin Reportar');

    worksheet.columns = [
      { header: 'Número', key: 'number', width: 20 },
      { header: 'Unit ID', key: 'unit_id', width: 20 },
      { header: 'Compañía', key: 'company_id', width: 20 },
      { header: 'Última Actualización', key: 'lastUpdate', width: 20 },
      { header: 'Días sin Reportar', key: 'daysSinceUpdate', width: 20 },
    ];

    unitsWithoutRecentUpdate.forEach((unit) => {
      worksheet.addRow({
        number: unit.number,
        unit_id: unit.unit_id || 'N/A',
        company_id: unit.mapon_company_name || 'N/A',
        lastUpdate: unit.lastUpdate ? new Date(unit.lastUpdate).toLocaleDateString() : 'N/A',
        daysSinceUpdate: unit.daysSinceUpdate + ' días',
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unidades_sin_reportar_${fileDate()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to generate page numbers with ellipsis
  const getPageNumbers = (totalPages: number, currentPage: number) => {
    const pages: (number | string)[] = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="w-full h-full overflow-auto p-4 sm:p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className={`text-2xl sm:text-3xl font-bold ${themeClasses.text}`}>
          Dashboard Mapon
        </h1>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          Vista general de la plataforma Mapon
        </p>
      </div>

      {/* Stats normalizados (API nueva) */}
      <div className="mb-6 sm:mb-8">
        <PlatformStatsCards platform="mapon" themeClasses={themeClasses} />
      </div>

      {/* Stats Cards (legacy, oculto) */}
      {false && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard
          icon={<i className="ri-truck-line text-white text-sm"></i>}
          label="Unidades"
          value={loading ? "..." : unitsCount}
          sublabel="Total"
          iconBgColor="bg-amber-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-user-line text-white text-sm"></i>}
          label="Usuarios"
          value={loading ? "..." : usersCount}
          sublabel="Total"
          iconBgColor="bg-blue-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-user-check-line text-white text-sm"></i>}
          label="Activos"
          value={loading ? "..." : activeUsers}
          sublabel={`de ${usersCount}`}
          iconBgColor="bg-green-500"
          themeClasses={themeClasses}
        />
      </div>
      )}

      {/* Distribución de riesgo por cliente */}
      <div className="mb-6 sm:mb-8">
        <PlatformRiskDistribution platform="mapon" themeClasses={themeClasses} />
      </div>

      {/* Charts legacy (Mapon API) — ocultos; uniformizado con V5/Wialon */}
      {false && (
      <>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Units by State */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Unidades por Estado</h3>
          <div className="space-y-3">
            {Object.entries(unitsByState).map(([state, count]) => (
              <div key={state} className="flex items-center justify-between">
                <span className={`text-sm ${themeClasses.textSecondary}`}>{state}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500"
                      style={{ width: `${(count / unitsCount) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${themeClasses.text}`}>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Units by Type */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Unidades por Tipo</h3>
          <div className="space-y-3">
            {Object.entries(unitsByType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className={`text-sm ${themeClasses.textSecondary}`}>{type}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${(count / unitsCount) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${themeClasses.text}`}>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Users by Type */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Usuarios por Tipo</h3>
          <div className="space-y-3">
            {Object.entries(usersByType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className={`text-sm ${themeClasses.textSecondary}`}>{type}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500"
                      style={{ width: `${(count / usersCount) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${themeClasses.text}`}>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Users Active vs Blocked */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Estado de Usuarios</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${themeClasses.textSecondary}`}>Activos</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(activeUsers / usersCount) * 100}%` }}
                  />
                </div>
                <span className={`text-sm font-medium ${themeClasses.text}`}>{activeUsers}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${themeClasses.textSecondary}`}>Bloqueados</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${(blockedUsers / usersCount) * 100}%` }}
                  />
                </div>
                <span className={`text-sm font-medium ${themeClasses.text}`}>{blockedUsers}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Units by Country */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Unidades por País</h3>
          <div className="space-y-3">
            {Object.entries(unitsByCountry).map(([country, count]) => (
              <div key={country} className="flex items-center justify-between">
                <span className={`text-sm ${themeClasses.textSecondary}`}>{country || 'Sin país'}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500"
                      style={{ width: `${(count / unitsCount) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${themeClasses.text}`}>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Units with Data vs Without Data */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Unidades con Datos vs Sin Datos</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${themeClasses.textSecondary}`}>Con datos</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(unitsWithData / unitsCount) * 100}%` }}
                  />
                </div>
                <span className={`text-sm font-medium ${themeClasses.text}`}>{unitsWithData}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${themeClasses.textSecondary}`}>Sin datos</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${(unitsWithoutData / unitsCount) * 100}%` }}
                  />
                </div>
                <span className={`text-sm font-medium ${themeClasses.text}`}>{unitsWithoutData}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Units by Company */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 col-span-2`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${themeClasses.text}`}>Unidades por Compañía</h3>
            <button
              onClick={downloadUnitsByCompanyExcel}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
            >
              <i className="ri-file-excel-2-line"></i>
              Descargar Excel
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${themeClasses.cardBorder}`}>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>ID Compañía</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Nombre Compañía</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Cantidad</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Porcentaje</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(unitsByCompany)
                  .slice((unitsByCompanyPage - 1) * itemsPerPage, unitsByCompanyPage * itemsPerPage)
                  .map(([company, count]) => (
                    <tr key={company} className={`border-b ${themeClasses.cardBorder}`}>
                      <td className={`py-2 px-3 ${themeClasses.text}`}>{company}</td>
                      <td className={`py-2 px-3 ${themeClasses.text}`}>{company}</td>
                      <td className={`py-2 px-3 ${themeClasses.text}`}>{count}</td>
                      <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{((count / unitsCount) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {Object.keys(unitsByCompany).length > itemsPerPage && (
            <div className="flex justify-center mt-6 space-x-2">
              <button
                onClick={() => setUnitsByCompanyPage(prev => Math.max(1, prev - 1))}
                disabled={unitsByCompanyPage === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Anterior
              </button>
              {getPageNumbers(Math.ceil(Object.keys(unitsByCompany).length / itemsPerPage), unitsByCompanyPage).map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === "number" && setUnitsByCompanyPage(page)}
                  disabled={page === "..."}
                  className={`px-3 py-1 rounded ${
                    page === unitsByCompanyPage
                      ? "bg-primary-500 text-white"
                      : page === "..."
                        ? "bg-gray-200 text-gray-400 cursor-default"
                        : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setUnitsByCompanyPage(prev => Math.min(Math.ceil(Object.keys(unitsByCompany).length / itemsPerPage), prev + 1))}
                disabled={unitsByCompanyPage === Math.ceil(Object.keys(unitsByCompany).length / itemsPerPage)}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Units Without Recent Update */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 mt-4 sm:mt-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${themeClasses.text}`}>
            Unidades sin reportar
          </h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <label className={`text-sm ${themeClasses.textSecondary}`}>Tiempo sin reportar:</label>
              <select
                value={timeFilter}
                onChange={(e) => {
                  setTimeFilter(e.target.value as '24h' | '1week' | '1month');
                  setUnitsWithoutUpdatePage(1);
                }}
                className={`px-2 py-1 rounded ${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} text-sm`}
              >
                <option value="24h">Más de 24 horas</option>
                <option value="1week">Más de 1 semana</option>
                <option value="1month">Más de 1 mes</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className={`text-sm ${themeClasses.textSecondary}`}>Ordenar por días:</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'desc' | 'asc')}
                className={`px-2 py-1 rounded ${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} text-sm`}
              >
                <option value="desc">Más días primero</option>
                <option value="asc">Menos días primero</option>
              </select>
            </div>
            <button
              onClick={downloadUnitsWithoutUpdateExcel}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
            >
              <i className="ri-file-excel-2-line"></i>
              Descargar Excel
            </button>
          </div>
        </div>
        {unitsWithoutRecentUpdate.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>Todas las unidades han reportado recientemente</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${themeClasses.cardBorder}`}>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Nombre</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Unit ID</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Compañía</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Última actualización</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Días sin reportar</th>
                  </tr>
                </thead>
                <tbody>
                  {unitsWithoutRecentUpdate
                    .slice((unitsWithoutUpdatePage - 1) * itemsPerPage, unitsWithoutUpdatePage * itemsPerPage)
                    .map((unit, index) => (
                      <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{unit.number}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{unit.unit_id || 'N/A'}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{unit.mapon_company_name || '—'}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{unit.lastUpdate ? new Date(unit.lastUpdate).toLocaleDateString() : 'N/A'}</td>
                        <td className={`py-2 px-3 ${themeClasses.text} font-medium text-red-500`}>{unit.daysSinceUpdate} días</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {unitsWithoutRecentUpdate.length > itemsPerPage && (
              <div className="flex justify-center mt-6 space-x-2">
                <button
                  onClick={() => setUnitsWithoutUpdatePage(prev => Math.max(1, prev - 1))}
                  disabled={unitsWithoutUpdatePage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  Anterior
                </button>
                {getPageNumbers(Math.ceil(unitsWithoutRecentUpdate.length / itemsPerPage), unitsWithoutUpdatePage).map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && setUnitsWithoutUpdatePage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-1 rounded ${
                      page === unitsWithoutUpdatePage
                        ? "bg-primary-500 text-white"
                        : page === "..."
                          ? "bg-gray-200 text-gray-400 cursor-default"
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setUnitsWithoutUpdatePage(prev => Math.min(Math.ceil(unitsWithoutRecentUpdate.length / itemsPerPage), prev + 1))}
                  disabled={unitsWithoutUpdatePage === Math.ceil(unitsWithoutRecentUpdate.length / itemsPerPage)}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      <div className="mt-6 sm:mt-8">
        <ActiveUnitsHistorySection
          themeClasses={themeClasses}
          platform="mapon"
          platformLabel="Mapon"
          showActiveUnits={false}
        />
      </div>

      <div className="mt-6 sm:mt-8">
        <AltasBajasPlatformCards themeClasses={themeClasses} platform="mapon" />
      </div>

      {/* Reportan / No reportan (API nueva) */}
      <div className="mt-4 sm:mt-6">
        <ReportingUnitsTables platform="mapon" themeClasses={themeClasses} />
      </div>

      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Todos los clientes
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Listado completo de clientes Mapon con su categoría de servicio
          </p>
        </div>
        {maponCompaniesQuery.isLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-64 animate-pulse`}></div>
        ) : (
          <AllClientsTable
            platformName="Mapon"
            companies={maponCompaniesQuery.data ?? []}
            themeClasses={themeClasses}
          />
        )}
      </div>

      {/* Sección: Alertas Críticas */}
      <div className="mt-6 sm:mt-8">
        {criticalAlerts.isLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            </div>
          </div>
        ) : (
          <PlatformAlerts
            companies={criticalCompanies}
            totalClients={maponCompaniesQuery.data?.length ?? 0}
            allUnits={allUnits}
            platformName="Mapon"
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
}
